import { Checkbox, FormControlLabel } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import api from "../../../../../features/auth/axiosConfig";
import EditMenu from "../../EditMenu";
import RoleSelect from "../../RoleSelect";
import LoadingScreen from "../../../../components/LoadingScreen";
import { filters } from "../utils/userOperations";
import { menu, handleAddCardClick } from "../utils/userHelpers";
import { UserContent } from "../edit-user/component/types";
import { handleCheckboxToggle } from "../utils/useHandlers";
import UserTable from "./UserTable";
import { handleSearch } from "../utils/useSearchValidation";
import CommonButtons from "src/shared/components/CommonButtons";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import { PageTitle } from "src/shared/PageTitle";
import { pageTitle } from "src/shared/utils/brand";
import DeleteConfirmation, { DeleteConfirmationHandle } from "../components/DeleteConfirmation";
import EmployeeModal from "src/business-types/grocery/layouts/setting/employee/AddEmployee";
import { User as EmployeeInterface } from "src/shared/services/interfacev2";
import { enrichUserData, enrichUsersData } from "src/shared/utils/userUtils";
import { getUser } from "src/shared/services/servicesv2";

function User() {
  const [employeeData, setEmployeeData] = useState<EmployeeInterface[]>([]);
  const [originalEmployeeData, setOriginalEmployeeData] = useState<EmployeeInterface[]>([]);
  const [, setUserContent] = useState<UserContent[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<EmployeeInterface[]>([]);
  const [isEditMode, setIsEditMode] = useState<EmployeeInterface | null>(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [, setPage] = useState(0);
  const [showRoleSelect, setShowRoleSelect] = useState(false);
  const [isBranchSelected, setIsBranchSelected] = useState(false);
  const [, setBusinessType] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [, setIsCheckboxClicked] = useState(false);
  const deleteModalRef = useRef<DeleteConfirmationHandle>(null);
  const [employee, setEmployee] = useState<boolean>(false);

  const { fontClass, borderHighlight, sidebarThemeClass } = useUserTheme();

  // Refresh user data with loading
  const refreshUserData = () => {
    setIsLoading(true);
    filters(
      showDeletedUsers,
      api,
      setUserContent,
      (data: React.SetStateAction<EmployeeInterface[]>) => {
        const dataArray = Array.isArray(data) ? data : [];
        const enrichedData = enrichUsersData(dataArray);
        setEmployeeData(enrichedData);
        setFilteredData(enrichedData);
        setOriginalEmployeeData(enrichedData);
        setIsLoading(false); // ✅ Set loading to false after success
      },
      setFilteredData,
      setIsLoading // ✅ Pass loading setter to filters
    );
  };

  useEffect(() => {
    const storedBusinessType = sessionStorage.getItem("business_type");
    setBusinessType(String(storedBusinessType));
  }, []);

  useEffect(() => {
    document.title = pageTitle("Users › Settings");
    refreshUserData();
  }, []);

  const handleAddCard = (id: string) => {
    handleAddCardClick(id, setSelectedUserId, setShowRoleSelect, setIsBranchSelected);
  };

  // Fixed handleEdit with proper error handling
  const handleEdit = (id: string) => {
    if (!id) {
      console.error("No ID provided for edit");
      return;
    }

    setIsLoading(true); // ✅ Set loading to true

    // Try to find the user in the data
    let editId: EmployeeInterface | undefined;
    
    // Search in employeeData
    editId = employeeData.find((i) => i._id?.$oid === id);
    
    // If not found, search in originalEmployeeData
    if (!editId) {
      editId = originalEmployeeData.find((i) => i._id?.$oid === id);
    }
    
    // If still not found, fetch directly from API
    if (!editId) {
      getUser(id)
        .then(response => {
          let userData = response.data;
          
          if (userData.result && Array.isArray(userData.result)) {
            userData = userData.result[0];
          }
          
          if (!userData) {
            console.error("No user data found in response");
            setIsLoading(false); // ✅ Set loading to false on error
            return;
          }
          
          if (!userData._id) {
            userData._id = { $oid: id };
          }
          
          // Enrich the data
          const enrichedData = enrichUserData(userData);
          
          setOriginalEmployeeData(prev => {
            const exists = prev.some(u => u._id?.$oid === id);
            if (exists) {
              return prev.map(u => u._id?.$oid === id ? enrichedData : u);
            }
            return [...prev, enrichedData];
          });
          
          setIsEditMode(enrichedData);
          setEmployee(true);
          setIsLoading(false); // ✅ Set loading to false after success
        })
        .catch(error => {
          console.error("Failed to fetch user:", error);
          setIsLoading(false); // ✅ Set loading to false on error
        });
      return;
    }
    
    const enrichedData = enrichUserData(editId);
    setIsEditMode(enrichedData);
    setEmployee(true);
    setIsLoading(false); // ✅ Set loading to false after success
  };

  const handleCheckboxClick = () => {
    handleCheckboxToggle(
      showDeletedUsers, 
      setShowDeletedUsers, 
      setIsCheckboxClicked, 
      setSearchTerm, 
      (value: boolean) => {
        setIsLoading(true); // ✅ Set loading to true before filter
        filters(
          value, 
          api, 
          setUserContent, 
          setEmployeeData, 
          setFilteredData,
          setIsLoading // ✅ Pass loading setter
        );
      }
    );
  };

  const handleDeleteUserClick = (id: string | number, username?: string) => {
    if (!id) return;
    deleteModalRef.current?.openModal(id, username ?? "");
  };

  const handleRoleSelectClose = () => {
    setShowRoleSelect(false);
    setSelectedUserId("");
    setIsBranchSelected(false);
  };

  return (
    <div className="p-1">
      <div className={`flex flex-col w-full h-[91vh] shadow-inner rounded-lg items-center ${sidebarThemeClass} overflow-hidden`}>
        <PageTitle title="- Settings › User" />
        <EditMenu editmenu={menu} />
        <div className="customer-toolbar flex items-center justify-end gap-4 p-2">
          <div className="user-toolbar">
            <div className="flex items-center" id="view-deleted-users">
              <FormControlLabel control={<Checkbox />} onClick={handleCheckboxClick} label="View deleted users" id="deleted-users-checkbox" />
            </div>
            <div className="user-search-input-wrap">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e, employeeData, setFilteredData, setSearchTerm, setPage)}
                placeholder="Search by Name or Phone number"
                className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass} text-sm text-black`}
              />
            </div>
            <div className="add-user-btn">
              <CommonButtons className="w-44 h-10" label="ADD USER" onClick={() => setEmployee(true)} />
            </div>
          </div>
        </div>
        <UserTable
          filteredData={filteredData}
          onEditUser={handleEdit}
          onAddCardClick={handleAddCard}
          isBranchSelected={isBranchSelected}
          onDeleteClick={handleDeleteUserClick}
          showDeletedUsers={showDeletedUsers}
        />
      </div>

      <EmployeeModal 
        key={isEditMode?._id?.$oid || 'new'}
        onClose={() => { 
          setEmployee(false); 
          setIsEditMode(null); 
        }} 
        open={employee} 
        editData={isEditMode} 
        onSuccess={refreshUserData} 
      />

      <DeleteConfirmation 
        ref={deleteModalRef} 
        onDeleteSuccess={refreshUserData}
      />
      
      {showRoleSelect && <RoleSelect id={selectedUserId} onClose={handleRoleSelectClose} />}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
      
      {/* ✅ Loading screen shows when isLoading is true */}
      {isLoading && <LoadingScreen message="Please wait, loading users..." />}
    </div>
  );
}

export default User;